import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import YatungButton from '@Src/_basic/components/YatungButton';
import YatungConfirmAlert from '@Src/_basic/components/YatungConfirmAlert';
import YatungDateSelect from '@Src/_basic/components/YatungDateSelect';
import YatungDialog from '@Src/_basic/components/YatungDialog';
import { PrintInfoProps, ProportionPosNameData } from '@Src/_basic/object/PrintProductionType';
import { FileApi } from '@Src/_basic/protocol/file/FileApi';
import { PrintProductionApi } from '@Src/_basic/protocol/printProduction/printProductionApi';
import { usePrintProduction } from '@Src/redux/printProduction/printProductionActions';
import { useDropzone } from "react-dropzone";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import html2pdf from 'html2pdf.js';
import moment from 'moment';
import React, { memo, useCallback, useRef, useState } from 'react';
import PrintProduction from './PrintProduction';

interface Props {
  open: boolean;
  onClose: () => void;
  info: PrintInfoProps;
}

enum PdfStatus {
  NONE = "none", ORIGIN = "origin", AUDITED = "audited",
}

//檢查是不是正確的日期並加入檔案名稱
const generateFilename = (info: PrintInfoProps, date: Date | null, pdfStatus: PdfStatus) => {
  if (!moment.isDate(date)) return `${info.factoryId}_${info.orderId.trim()}_unknown_date.pdf`;
  if (pdfStatus === PdfStatus.ORIGIN) return `${info.factoryId}_${info.orderId.trim()}_${moment(date).format('YYYY-MM-DD')}_origin.pdf`;
  return `${info.factoryId}_${info.orderId.trim()}_${moment(date).format('YYYY-MM-DD')}.pdf`;
}

const PrintSelectProportion = ({ open, onClose, info }: Props) => {
  const {
    setProportionPosition,
    proportionPosition,
    setAmsAbnormalLegalData,
    setConcreteExptReportData,
    setBomData,
    setCarbonData,
    setFilename
  } = usePrintProduction();

  const componentRef = useRef(null);

  const [date, setDate] = useState<Date | null>(new Date());
  const [proportionOptions, setProportionOptions] = useState<Record<string, string>>({});
  const [isShowAlert, setIsShowAlert] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<PdfStatus>(PdfStatus.NONE);
  const [files, setFiles] = useState<File[]>([]);

  const handleSelectDate = useCallback(
    (date: Date | null) => {
      setDate(date);
      setFiles([]);
      PrintProductionApi.getProjectProps(
        {
          ...info,
          shipmentTimestamp: new Date(date as Date).getTime(),
        },
        (proportionDisplayName: Array<ProportionPosNameData>) => {
          setProportionOptions(proportionDisplayName.reduce((options, proportionData) => ({ ...options, [proportionData.displayName]: '' }), {}));
          setProportionPosition(proportionDisplayName.reduce((acc, cur) => ({ ...acc, [cur.displayName]: cur }), {}));
        },
      );
      FileApi.checkPdfFile(generateFilename(info, date, PdfStatus.ORIGIN), (res) => {
        if (res) return setPdfStatus(PdfStatus.AUDITED);
        FileApi.checkPdfFile(generateFilename(info, date, PdfStatus.NONE), (res) => {
          if (res) return setPdfStatus(PdfStatus.ORIGIN);
          setPdfStatus(PdfStatus.NONE);
        });
      });

    },
    [info],
  );

  const handleInput = useCallback(
    (proportionName: string, value: string) => {
      if (!proportionPosition) return;
      const data = { ...proportionPosition[proportionName], position: value };
      setProportionPosition({ ...proportionPosition, [proportionName]: data });
    },
    [setProportionPosition, proportionPosition],
  );

  const handleClose = useCallback(() => {
    setProportionPosition({});
    setProportionOptions({});
    onClose();
  }, [setProportionPosition, onClose]);

  const handleSubmitPrint = useCallback(() => {
    if (!proportionPosition) return;

    const error = Object.keys(proportionPosition).find((key) => !proportionPosition[key]);
    if (error) {
      setIsShowAlert(true);
      return;
    }

    const startOfDay = moment(date).startOf('day').toDate();
    const endOfDay = moment(date).endOf('day').toDate();

    Promise.all([
      PrintProductionApi.getAMSAbnormalLegal({
        factoryId: info.factoryId,
        exportTime: new Date(date as Date).getTime(),
      }),
      PrintProductionApi.getConcreteExptReport({
        ...info,
        startTime: new Date(startOfDay).getTime(),
        endTime: new Date(endOfDay).getTime(),
        realPropNames: Object.keys(proportionPosition),
      }),
      PrintProductionApi.getBom({
        ...info,
      }),
      PrintProductionApi.getCarbon({
        startTime: new Date(startOfDay).getTime(),
        endTime: new Date(endOfDay).getTime(),
        orderId: info.orderId,
      }),
    ])
      .then(([abnormalLegal, concreteExptReport, bomData, carbonData]) => {
        if (abnormalLegal) setAmsAbnormalLegalData(abnormalLegal);
        if (concreteExptReport) setConcreteExptReportData(concreteExptReport);
        if (bomData) setBomData(bomData);
        if (carbonData) setCarbonData(carbonData);
        setFilename(generateFilename(info, date, PdfStatus.NONE));
      })
      .then(async () => {
        const opt = {
          filename: 'test.pdf',
          html2canvas: { dpi: 96, letterRendering: true, useCORS: true, scale: 2 },
          pagebreak: { mode: 'avoid-all' },
        };

        const pdf = html2pdf().set(opt).from(componentRef.current).toPdf();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const blobdata = await pdf.output('blob');
        FileApi.uploadPdfFile(generateFilename(info, date, PdfStatus.NONE), blobdata, () => {
          setPdfStatus(PdfStatus.ORIGIN);
          pdf.save(generateFilename(info, date, PdfStatus.NONE));
        });
      });
  }, [
    proportionPosition,
    setAmsAbnormalLegalData,
    info,
    date,
    setConcreteExptReportData,
    setBomData,
    setCarbonData,
    setFilename
  ]);

  const handleDownloadPdf = useCallback(() => {
    FileApi.downloadPdfFile(generateFilename(info, date, PdfStatus.NONE));
  }, [info, date]);

  const handleUpdateProduction = useCallback(async () => {
    const originFilename = generateFilename(info, date, PdfStatus.NONE);
    FileApi.auditedPdfFile(originFilename, files[0], (isSuccess) => {
      if (isSuccess) return setPdfStatus(PdfStatus.AUDITED);
      setPdfStatus(PdfStatus.ORIGIN);
    });
  }, [files, info, date]);

  const onCloseAlert = useCallback(() => {
    setIsShowAlert(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [] }, // 只允許圖片上傳
    maxFiles: 3, // 最多 3 個檔案
    maxSize: 5 * 1024 * 1024, // 5MB 限制
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles);
    },
  });

  return (
    <>
      <YatungDialog
        open={open}
        handleClose={handleClose}
        title={'履歷匯出'}
        body={
          <Stack gap={3.5} pt={3}>
            <Stack justifyContent="center" alignItems="center">
              <YatungDateSelect onChange={handleSelectDate} value={date} label="履歷日期" />
            </Stack>
            {[PdfStatus.NONE].includes(pdfStatus) && <Stack
              sx={{
                border: '1px solid #aaa',
                p: 3,
                maxHeight: '50vh',
                overflowY: 'auto',
                boxShadow: '4px 4px 20px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Grid container sx={{ borderBottom: '1px solid #ccc', pb: 3, pt: 1 }}>
                <Grid item xs={6}>
                  <Typography>配比編號</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>澆置位置</Typography>
                </Grid>
              </Grid>
              {Object.keys(proportionOptions).map((item) => (
                <Grid
                  key={item}
                  container
                  sx={{
                    borderBottom: '1px solid #ccc',
                    py: 3,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: 400,
                  }}
                  spacing={1}
                >
                  <Grid item xs={5}>
                    <Typography>{item}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <TextField
                      variant="standard"
                      label={`${item}澆置位置`}
                      onChange={(e) => handleInput(item, e.target.value)}
                    />
                  </Grid>
                </Grid>
              ))}
            </Stack>}
            {[PdfStatus.ORIGIN].includes(pdfStatus) && <Stack>
              <Paper
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "grey.400",
                  padding: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.dark" },
                }}
              >
                <input {...getInputProps()} />
                {files.length == 0 && <CloudUploadIcon sx={{ fontSize: 48, color: "grey.400" }} />}
                {files.length > 0 && <PictureAsPdfIcon sx={{ fontSize: 48, color: "#003087" }} />}

                {files.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="subtitle1">已選擇的檔案：</Typography>
                    {files.map((file) => (
                      <Typography key={file.name} variant="body2">
                        {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      </Typography>
                    ))}
                  </Box>
                )}

                <Typography variant="h6" mt={2}>
                  {isDragActive ? "釋放PDF來上傳" : "拖曳PDF到這裡，或點擊選擇"}
                </Typography>
              </Paper>
            </Stack>}
            <Stack gap={4} flexDirection="row" justifyContent="flex-end">
              <YatungButton width={100} color="red" text="取消" onClick={handleClose} />
              {[PdfStatus.ORIGIN].includes(pdfStatus) && <YatungButton width={130} color="blue" text="更新履歷" onClick={handleUpdateProduction} disabled={files.length == 0} />}
              {[PdfStatus.ORIGIN, PdfStatus.AUDITED].includes(pdfStatus) && <YatungButton width={130} color="blue" text="下載履歷" onClick={handleDownloadPdf} />}
              {[PdfStatus.NONE].includes(pdfStatus) && <YatungButton width={140} color="blue" text="創建新履歷" onClick={handleSubmitPrint} />}
            </Stack>
          </Stack>
        }
      ></YatungDialog>
      <YatungConfirmAlert isShowAlert={isShowAlert} onClose={onCloseAlert} alertText="請輸入澆置位置" />
      <Stack sx={{ display: 'none' }}>
        <PrintProduction ref={componentRef} title={info.engName} time={moment(date).format('YYYY/MM/DD')} />
      </Stack>
    </>
  );
};

export default memo(PrintSelectProportion);
